/**
 * Encoder Setup Module
 *
 * Handles creation and configuration of video/audio encoders and muxer.
 * Extracted from exporter.js for better separation of concerns.
 */

import {
  VIDEO_CONFIG,
  AUDIO_CONFIG,
  CHUNKING_CONFIG,
  getCodecCandidates,
} from './export-config.js';
import { ENCODER_CONFIG } from './export-performance.js';

/**
 * Select the best available video codec with hardware acceleration
 * @param {Object} options
 * @param {number} options.width - Video width
 * @param {number} options.height - Video height
 * @param {number} options.bitrate - Video bitrate
 * @param {number} options.fps - Frames per second
 * @param {string} options.preset - Codec preset (auto, main, baseline, high, hevc)
 * @returns {Promise<{codec: string, name: string, hardwareSupported: boolean}>}
 */
export async function selectVideoCodec(options) {
  const { width, height, bitrate, fps, preset = 'auto' } = options;
  const candidates = getCodecCandidates(preset);

  let selectedCodec = candidates[0].codec;
  let selectedName = candidates[0].name;
  let hardwareSupported = false;

  // Phase 1: Try hardware-accelerated encoding
  for (const candidate of candidates) {
    const testConfig = {
      codec: candidate.codec,
      width,
      height,
      bitrate,
      framerate: fps,
      hardwareAcceleration: 'prefer-hardware',
    };

    try {
      const support = await VideoEncoder.isConfigSupported(testConfig);
      console.log(
        `[Encoder] Codec ${candidate.name} (${candidate.codec}): hw supported=${support.supported}`,
        support.config
      );

      if (support.supported) {
        selectedCodec = candidate.codec;
        selectedName = candidate.name;
        hardwareSupported = true;
        console.log(
          `[Encoder] Selected codec: ${candidate.name} (${candidate.codec}) with hardware acceleration`
        );
        break;
      }
    } catch (e) {
      console.warn(`[Encoder] Failed to check codec ${candidate.name} (hw):`, e);
    }
  }

  // Phase 2: Software fallback — try without hardware preference
  if (!hardwareSupported) {
    console.warn('[Encoder] No hardware-accelerated codec found, trying software fallback');

    for (const candidate of candidates) {
      const testConfig = {
        codec: candidate.codec,
        width,
        height,
        bitrate,
        framerate: fps,
        hardwareAcceleration: 'no-preference',
      };

      try {
        const support = await VideoEncoder.isConfigSupported(testConfig);
        if (support.supported) {
          selectedCodec = candidate.codec;
          selectedName = candidate.name;
          console.log(
            `[Encoder] Selected codec: ${candidate.name} (${candidate.codec}) via software fallback`
          );
          break;
        }
      } catch (e) {
        console.warn(`[Encoder] Failed to check codec ${candidate.name} (sw):`, e);
      }
    }
  }

  return {
    codec: selectedCodec,
    name: selectedName,
    hardwareSupported,
  };
}

/**
 * Create and configure a video encoder
 * @param {Object} options
 * @param {number} options.width - Video width
 * @param {number} options.height - Video height
 * @param {number} options.bitrate - Video bitrate
 * @param {number} options.fps - Frames per second
 * @param {string} options.codec - Codec string (from selectVideoCodec)
 * @param {Function} options.onOutput - Callback for encoded chunks
 * @param {Function} options.onError - Callback for errors
 * @param {ResourceRegistry} [registry] - Optional resource registry for tracking
 * @returns {Promise<VideoEncoder>}
 */
export async function createVideoEncoder(options, registry = null) {
  const { width, height, bitrate, fps, codec, onOutput, onError } = options;

  const encoder = new VideoEncoder({
    output: onOutput,
    error: onError || ((e) => console.error('Video encoder error:', e)),
  });

  // Track encoder in registry if provided
  if (registry) {
    registry.track(encoder, {
      name: 'VideoEncoder',
      type: 'VideoEncoder',
      category: 'webcodecs_encoder',
    });
  }

  const baseConfig = {
    codec,
    width,
    height,
    bitrate,
    framerate: fps,
    latencyMode: ENCODER_CONFIG.latencyMode,
    hardwareAcceleration: 'prefer-hardware',
  };

  console.log('[Encoder] VideoEncoder config:', baseConfig);

  try {
    encoder.configure(baseConfig);
    console.log('[Encoder] VideoEncoder configured successfully');
  } catch (err) {
    // Fallback for older browsers that don't accept optional flags
    console.warn('[Encoder] VideoEncoder configure fallback without acceleration flags', err);
    encoder.configure({
      codec: baseConfig.codec,
      width: baseConfig.width,
      height: baseConfig.height,
      bitrate: baseConfig.bitrate,
      framerate: baseConfig.framerate,
    });
  }

  return encoder;
}

/**
 * Create and configure an audio encoder
 * @param {Object} options
 * @param {number} options.sampleRate - Audio sample rate
 * @param {number} options.numberOfChannels - Number of audio channels
 * @param {number} options.bitrate - Audio bitrate (defaults to AUDIO_CONFIG.defaultBitrate)
 * @param {Function} options.onOutput - Callback for encoded chunks
 * @param {Function} options.onError - Callback for errors
 * @param {ResourceRegistry} [registry] - Optional resource registry for tracking
 * @returns {AudioEncoder}
 */
export function createAudioEncoder(options, registry = null) {
  const {
    sampleRate,
    numberOfChannels,
    bitrate = AUDIO_CONFIG.defaultBitrate,
    onOutput,
    onError,
  } = options;

  const encoder = new AudioEncoder({
    output: onOutput,
    error: onError || ((e) => console.error('Audio encoder error:', e)),
  });

  // Track encoder in registry if provided
  if (registry) {
    registry.track(encoder, {
      name: 'AudioEncoder',
      type: 'AudioEncoder',
      category: 'webcodecs_encoder',
    });
  }

  encoder.configure({
    codec: AUDIO_CONFIG.codec,
    numberOfChannels,
    sampleRate,
    bitrate,
  });

  return encoder;
}

/**
 * Create a muxer for video/audio output
 * @param {Object} options
 * @param {number} options.width - Video width
 * @param {number} options.height - Video height
 * @param {number} options.sampleRate - Audio sample rate
 * @param {number} options.numberOfChannels - Number of audio channels
 * @param {boolean} options.streaming - Use streaming mode (FSA) vs in-memory
 * @param {FileSystemWritableFileStream} options.writableStream - Stream for FSA mode
 * @param {Function} options.onBytesWritten - Callback for bytes written (streaming mode)
 * @param {Array} options.chunks - Array to collect chunks (in-memory mode)
 * @param {Array} options.writeQueue - Array to queue write operations (FSA mode)
 * @returns {Object} Muxer instance
 */
export function createMuxer(options) {
  const {
    width,
    height,
    sampleRate,
    numberOfChannels,
    streaming,
    writableStream,
    onBytesWritten,
    chunks,
    writeQueue,
  } = options;

  // Ensure Mp4Muxer is available
  if (!window.Mp4Muxer) {
    throw new Error('Mp4Muxer not loaded');
  }

  if (streaming && writableStream) {
    console.log('[Muxer] Using FSA streaming mode');
    let totalBytesWritten = 0;

    return new Mp4Muxer.Muxer({
      target: new Mp4Muxer.StreamTarget({
        onData: (data, position) => {
          // Queue write operation (onData is sync, but write is async)
          writeQueue.push(
            writableStream.write({ type: 'write', position, data })
          );
          totalBytesWritten += data.byteLength;
          if (onBytesWritten) {
            onBytesWritten(totalBytesWritten);
          }
        },
        chunked: true,
        chunkSize: CHUNKING_CONFIG.fsaChunkSize,
      }),
      video: {
        codec: 'avc',
        width,
        height,
      },
      audio: {
        codec: 'aac',
        numberOfChannels,
        sampleRate,
      },
      fastStart: false, // Disable for streaming
    });
  }

  // In-memory mode with chunk collection
  console.log('[Muxer] Using in-memory chunked mode');
  let inMemorySize = 0;

  return new Mp4Muxer.Muxer({
    target: new Mp4Muxer.StreamTarget({
      onData: (data, position) => {
        // Store chunk with position for proper ordering
        // Make a copy since the buffer may be reused
        const chunk = new Uint8Array(data);
        chunks.push({ data: chunk, position });
        inMemorySize += chunk.byteLength;
        if (onBytesWritten) {
          onBytesWritten(inMemorySize);
        }
      },
      chunked: true,
      chunkSize: CHUNKING_CONFIG.memoryChunkSize,
    }),
    video: {
      codec: 'avc',
      width,
      height,
    },
    audio: {
      codec: 'aac',
      numberOfChannels,
      sampleRate,
    },
    fastStart: false, // Disable for streaming mode
  });
}

/**
 * Assemble in-memory chunks into a Blob
 * @param {Array} chunks - Array of {data: Uint8Array, position: number}
 * @returns {Blob} Video blob
 */
export function assembleChunksToBlob(chunks) {
  // Sort by position to ensure correct order
  chunks.sort((a, b) => a.position - b.position);

  // Create blob from sorted chunks
  const blobParts = chunks.map(chunk => chunk.data);
  return new Blob(blobParts, { type: 'video/mp4' });
}

/**
 * Convert AudioBuffer to AudioData for WebCodecs
 * @param {AudioBuffer} audioBuffer - Source audio buffer
 * @param {number} startTimestampMicros - Start timestamp in microseconds
 * @param {ResourceRegistry} [registry] - Optional resource registry for tracking
 * @returns {AudioData}
 */
export function audioBufferToAudioData(audioBuffer, startTimestampMicros = 0, registry = null) {
  const numberOfChannels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const sampleRate = audioBuffer.sampleRate;

  // Get planar audio data (channels stored sequentially)
  const planarData = new Float32Array(length * numberOfChannels);
  for (let channel = 0; channel < numberOfChannels; channel++) {
    const channelData = audioBuffer.getChannelData(channel);
    planarData.set(channelData, channel * length);
  }

  const audioData = new AudioData({
    format: 'f32-planar',
    sampleRate,
    numberOfFrames: length,
    numberOfChannels,
    timestamp: startTimestampMicros,
    data: planarData,
  });

  // Track in registry if provided (caller should dispose after encode)
  if (registry) {
    registry.track(audioData, {
      name: `AudioData@${startTimestampMicros}`,
      type: 'AudioData',
      category: 'webcodecs_frame',
    });
  }

  return audioData;
}

/**
 * Load mp4-muxer from CDN with timeout
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<void>}
 */
export async function loadMuxer(timeout = 10000) {
  // Check if already loaded
  if (window.Mp4Muxer) {
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/mp4-muxer@5.1.3/build/mp4-muxer.min.js';
  document.head.appendChild(script);

  await new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      script.remove();
      reject(new Error(`CDN load timeout after ${timeout}ms`));
    }, timeout);

    script.onload = () => {
      clearTimeout(timeoutId);
      script.remove();
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timeoutId);
      script.remove();
      reject(new Error('Failed to load mp4-muxer from CDN'));
    };
  });
}

/**
 * Detect if software encoder fallback is being used
 * @param {string} codec - Codec string
 * @param {Object} config - Encoder config used
 * @returns {Promise<boolean>} True if software fallback detected
 */
export async function detectSoftwareFallback(codec, config) {
  try {
    const testConfig = {
      codec,
      width: config.width,
      height: config.height,
      bitrate: config.bitrate,
      framerate: config.fps,
      hardwareAcceleration: 'prefer-hardware',
    };

    const result = await VideoEncoder.isConfigSupported(testConfig);

    // Check if the returned config indicates software mode
    if (result.config && result.config.hardwareAcceleration) {
      const hw = result.config.hardwareAcceleration;
      if (hw === 'no-preference' || hw === 'prefer-software') {
        console.warn('[Encoder] Software encoder fallback detected');
        return true;
      }
    }

    return false;
  } catch (e) {
    console.warn('[Encoder] Could not detect software fallback:', e);
    return false;
  }
}
